import React from "react"
import { STATUS } from "./utils/constants/statuses"
import { resolveUrlForArticles, careerPathToSlug } from "./utils/urlUtils"
import { interpolate, getCurrentUnixTimestamp } from "./utils/displayUtils"
import {
  getCurriculumCode,
  buildCanvasUrlForRequirement,
  CANVAS_URLS,
} from "./utils/constants/externalUrls"
import { CAREERPATH_EXECMGMT } from "../../Education/Enrollment/utils"
import { LEVEL, REQUIREMENT } from "./utils/constants/levels"
import { useMembershipJourneyContext } from "./MembershipJourneyContext"
import {
  EDUCATION_PGM_LEVEL_PURCHASE_PATH,
  getEducationPurchaseLinkProps,
  getLevel2PurchasePrimaryLinkText,
  useNavigateToEducationPgmPurchase,
  useResolveEducationPgmPurchaseUrl,
} from "../../../utils/educationPgmPurchase"
import { flags, useFeatureFlags } from "../../../lib/feature-flags"
import {
  RequirementActions,
  ActionButton,
  ActionButtonText,
  TextButton,
} from "./Styles/ProgressTracker.styles"

const RequirementActionLinks = ({
  displayData,
  requirementStatus,
  activeLevel,
  requirementKey,
}) => {
  const { pgaId, pgmProgress } = useMembershipJourneyContext()
  const resolveEducationPgmPurchaseUrl = useResolveEducationPgmPurchaseUrl()
  const navigateToPurchase = useNavigateToEducationPgmPurchase()
  const [isAccountPurchaseEnabled = false] =
    useFeatureFlags([flags.FLAG_FEAT_EDUCATION_PGM_LEVEL_PURCHASE]) ?? []
  const careerPath = pgmProgress?.careerPath
  const curriculumVersion = pgmProgress?.curriculumVersion || "3.1"

  if (!displayData) {
    return null
  }

  const hasPrimaryLink =
    displayData.primaryLinkText && displayData.primaryLinkUrl
  const hasSecondaryLink =
    displayData.secondaryLinkText && displayData.secondaryLinkUrl
  const hasActionLink = hasPrimaryLink || hasSecondaryLink
  const isActionable = requirementStatus !== STATUS.LOCKED

  if (!hasActionLink || !isActionable) {
    return null
  }

  const unixTimestamp = getCurrentUnixTimestamp()

  const slug = careerPathToSlug(careerPath)
  const careerPathSlug = slug ? `${slug}-` : ""
  const curriculumCode = getCurriculumCode(curriculumVersion)
  const careerPathName = encodeURIComponent(
    careerPath || CAREERPATH_EXECMGMT.careerPathName,
  )

  const activeLevelData = pgmProgress?.levels?.find(
    level => level.key === activeLevel,
  )
  const levelCode = activeLevelData?.levelCode

  const canvasUrl =
    levelCode && requirementKey
      ? buildCanvasUrlForRequirement(activeLevel, levelCode, requirementKey)
      : null

  const interpolationContext = {
    pgaId,
    unixTimestamp,
    careerPathSlug,
    careerPathName,
    curriculumCode,
    canvasUrl: canvasUrl || CANVAS_URLS.HOME,
  }

  const primaryUrl = resolveEducationPgmPurchaseUrl(
    resolveUrlForArticles(
      interpolate(displayData.primaryLinkUrl, interpolationContext),
    ),
  )
  const secondaryUrl = resolveUrlForArticles(
    interpolate(displayData.secondaryLinkUrl, interpolationContext),
  )
  const primaryLinkProps =
    primaryUrl === EDUCATION_PGM_LEVEL_PURCHASE_PATH
      ? {
          href: primaryUrl,
          onClick: event => {
            event.preventDefault()
            navigateToPurchase()
          },
        }
      : getEducationPurchaseLinkProps(primaryUrl)
  const primaryLinkText =
    activeLevel === LEVEL.LEVEL_1 && requirementKey === REQUIREMENT.PURCHASE
      ? getLevel2PurchasePrimaryLinkText(isAccountPurchaseEnabled)
      : displayData.primaryLinkText

  return (
    <RequirementActions>
      {hasPrimaryLink && (
        <ActionButton as="a" {...primaryLinkProps}>
          <ActionButtonText>{primaryLinkText}</ActionButtonText>
        </ActionButton>
      )}
      {hasSecondaryLink && (
        <TextButton
          as="a"
          href={secondaryUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {displayData.secondaryLinkText}
        </TextButton>
      )}
    </RequirementActions>
  )
}

export default RequirementActionLinks
